import { useEffect, useRef } from 'react';
import { Button, Spin } from 'antd';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ActivityCommentIcon } from '@/assets/icons/icons';
import {
  GetShipmentAlertActivity,
  MarkShipmentAlertActivitySeen,
} from '@/apis/shipment';
import { useToken } from '@/common/helper';
import AlertSeverityIcon from '../AlertSeverityIcon/AlertSeverityIcon';
import {
  formatAlertOwners,
  formatAlertRelativeTime,
  getAlertPillLabel,
} from '../alertActivityUtils';
import styles from './AlertActivityPopover.module.css';
import { CustomShipmentText } from '@/components';
import Pill from '@/pages/Shipments/AiTranscripts/ui/Pill';

interface AlertActivityPopoverProps {
  shipmentId: string;
  /** activity_version from the list row; sent back when the popover opens. */
  activityVersion: number;
  open: boolean;
}

/**
 * The shared Pill has no "To review" colour, and other screens rely on how it
 * maps labels, so the open state is coloured here with existing theme tokens
 * (they switch with light and dark mode).
 */
const TO_REVIEW_PILL_STYLE = {
  background: 'var(--customerleg-orange-thin)',
  color: 'var(--customerleg-orange)',
};

export const alertActivityQueryKey = (
  shipmentId: string,
  activityVersion: number
) => ['shipment-alert-activity', shipmentId, activityVersion] as const;

const AlertActivityPopover = ({
  shipmentId,
  activityVersion,
  open,
}: AlertActivityPopoverProps) => {
  const { token } = useToken();
  const seenVersionRef = useRef<number | null>(null);

  const query = useInfiniteQuery({
    queryKey: alertActivityQueryKey(shipmentId, activityVersion),
    queryFn: async ({ pageParam }) => {
      const response = await GetShipmentAlertActivity(
        token ?? '',
        shipmentId,
        pageParam
      );
      return response.data.data.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_cursor : undefined,
    enabled: open,
  });

  // Opening the popover marks the activity as seen, but does not acknowledge
  // the alerts: only explicitly opening an alert panel does that.
  useEffect(() => {
    if (!open) {
      seenVersionRef.current = null;
      return;
    }
    if (seenVersionRef.current === activityVersion) return;
    seenVersionRef.current = activityVersion;
    MarkShipmentAlertActivitySeen(token ?? '', shipmentId, {
      version: activityVersion,
      acknowledge: false,
    }).catch((): void => undefined);
  }, [open, activityVersion, shipmentId, token]);

  const rows = query.data?.pages.flatMap((page) => page.results) ?? [];

  return (
    <div className={styles.popover} role='dialog' aria-label='Alert activity'>
      {query.isLoading ? (
        <div className={styles.status}>
          <Spin size='small' />
        </div>
      ) : null}

      {query.isError ? (
        <div className={styles.status}>
          Unable to load alerts.{' '}
          <Button type='link' size='small' onClick={() => query.refetch()}>
            Retry
          </Button>
        </div>
      ) : null}

      {!query.isLoading && !query.isError && rows.length === 0 ? (
        <div className={styles.status}>No alerts</div>
      ) : null}

      {rows?.map((row) => {
        const pillLabel = getAlertPillLabel(row);
        const owners = formatAlertOwners(row.owners);
        const time = formatAlertRelativeTime(row.occurred_at);
        return (
          <div className={styles.row} key={row.event_key}>
            <div className={styles.main}>
              <span className={styles.icon}>
                {/* Closed alerts keep their severity icon, as in the design. */}
                <AlertSeverityIcon severity={row.severity} />
              </span>
              <div className={styles.text}>
                <CustomShipmentText text={row?.title} fontWeight={600} />
                <span className={styles.meta}>
                  <span className={styles.owner}>
                    {owners ? `${time}, ${owners}` : time}
                  </span>
                  {row.comment_count > 0 ? (
                    <span
                      className={styles.comments}
                      aria-label={`${row.comment_count} comments`}
                    >
                      <ActivityCommentIcon
                        filled
                        width={10}
                        height={10}
                        color='var(--text-tertiary)'
                      />
                      {row.comment_count}
                    </span>
                  ) : null}
                </span>
              </div>
            </div>
            <div className={styles.pillColumn}>
              {/* The shared Pill picks its colours and icon from the label. */}
              <Pill
                style={{
                  whiteSpace: 'normal',
                  flexShrink: 1,
                  maxWidth: '100%',
                  ...(row.state === 'OPEN' ? TO_REVIEW_PILL_STYLE : {}),
                }}
              >
                {pillLabel}
              </Pill>
            </div>
          </div>
        );
      })}

      {query.hasNextPage ? (
        <Button
          className={styles.more}
          type='link'
          size='small'
          loading={query.isFetchingNextPage}
          onClick={() => query.fetchNextPage()}
        >
          Show more
        </Button>
      ) : null}
    </div>
  );
};

export default AlertActivityPopover;
